import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { API_BASE } from '../../config/api';
import { paymentService, type PaymentProvider } from '../../services/paymentService';

/** Phải khớp MAX_QR_BYTES ở server — chặn sớm để khỏi gửi đi rồi mới bị từ chối. */
const MAX_BYTES = 512 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp';

interface Props {
  provider: PaymentProvider;
  onChanged: () => void;
  onToast: (msg: string, sev: 'success' | 'error') => void;
}

const kb = (n: number): string => `${Math.round(n / 1024)} KB`;

/**
 * Tải ảnh QR cho cổng Binance Pay.
 *
 * Ảnh nằm ở KV phía server, không nhét vào `publicConfig` — config đó đi kèm mọi
 * phản hồi của /topup và mỗi lượt poll của /pay, nên một ảnh 40 KB sẽ bị gửi lặp
 * lại vài giây một lần.
 */
export const QrUpload: React.FC<Props> = ({ provider, onChanged, onToast }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const version = provider.config?.qrVersion;
  const pastedUrl = provider.config?.qrImageUrl;
  // `qrImageUrl` của bản chiếu admin là URL tương đối do server sinh; ghép với
  // API_BASE vì trang admin nằm ở tên miền khác với API.
  const src = version
    ? `${API_BASE}/pay/qr/${provider.id}?v=${version}`
    : pastedUrl || '';

  const pick = () => inputRef.current?.click();

  const onFile = async (file: File) => {
    if (file.size > MAX_BYTES) {
      onToast(`Ảnh ${kb(file.size)} — vượt mức cho phép ${kb(MAX_BYTES)}. Chụp lại hoặc nén nhỏ hơn.`, 'error');
      return;
    }
    setBusy(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(new Error('Không đọc được file'));
        fr.readAsDataURL(file);
      });
      const r = await paymentService.uploadQr(provider.id, base64);
      onToast(`Đã tải ảnh QR (${kb(r.bytes)}, ${r.contentType})`, 'success');
      onChanged();
    } catch (e) {
      // Server từ chối bằng tên lỗi ngắn — dịch sang câu nói rõ phải làm gì.
      const raw = (e as Error).message;
      const msg =
        raw.includes('not_an_image') ? 'File này không phải ảnh PNG/JPEG/WEBP. SVG cũng không nhận vì nó chạy được mã.'
        : raw.includes('file_too_large') ? `Ảnh vượt ${kb(MAX_BYTES)}.`
        : raw.includes('invalid_base64') || raw.includes('empty_file') ? 'File hỏng hoặc rỗng.'
        : raw;
      onToast(msg, 'error');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await paymentService.removeQr(provider.id);
      onToast('Đã xoá ảnh QR. Trang thanh toán sẽ chỉ còn Binance ID và Note.', 'success');
      onChanged();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, mb: 0.5, mt: 1 }}
      >
        Ảnh QR
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
        <Box
          sx={{
            width: 132,
            height: 132,
            flex: 'none',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'action.hover',
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
          }}
        >
          {src ? (
            <Box component="img" src={src} alt="QR" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', px: 1 }}>
              Chưa có ảnh
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Mở app Binance → Pay → nhận tiền, chụp hoặc lưu ảnh QR của bạn rồi tải lên đây.
            Khách sẽ thấy ảnh này ở trang nạp điểm và trang thanh toán. Nhận PNG, JPEG, WEBP, tối đa{' '}
            {kb(MAX_BYTES)}.
          </Typography>

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={pick}
              disabled={busy}
            >
              {version ? 'Đổi ảnh' : 'Tải ảnh lên'}
            </Button>
            {version && (
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => void remove()} disabled={busy}>
                Xoá
              </Button>
            )}
          </Stack>

          {!version && pastedUrl && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Đang dùng ảnh từ URL dán tay. Tải ảnh lên sẽ thay thế nó.
            </Alert>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
